// src/router/index.js
import axios from "@/axiosInstance";
import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { listiniRedirectFor } from "@/config/listiniCategories";

// --- Pagine (come nel tuo file originale) ---
import NotFound from "../components/notFound.vue";
import LoginPage from "../views/LoginPage.vue";
import SendEmailPage from "../views/SendEmailPage.vue";
import SetNewPasswordPage from "../views/SetNewPasswordPage.vue";

import AccountPage from "../views/AccountPage.vue";
import HomePage from "../views/HomePage.vue";

import ClientPage from "../views/ClientPage.vue";
import CreateClientPage from "../views/CreateClientPage.vue";
import EditClientPage from "../views/EditClientPage.vue";

import CreateItemPage from "../views/CreateItemPage.vue";
import EditItemPage from "../views/EditItemPage.vue";
import ListiniPage from "../views/ListiniPage.vue";
import ListiniCategoryPage from "../views/ListiniCategoryPage.vue";
import MagazzinoPage from "../views/MagazzinoPage.vue";

import CreateQuotePage from "../views/CreateQuotePage.vue";
import EditQuotePage from "../views/EditQuotePage.vue";
import QuotePage from "../views/QuotePage.vue";

import OrderPage from "../views/OrderPage.vue";
import OrdiniPage from "../views/OrdiniPage.vue";

// ---------------------------------------------------------------------------
// Endpoint BE per verificare il cookie JWT (deve rispondere 200 o 401/403)
// ---------------------------------------------------------------------------
const WHO_AM_I = "/auth/me";

// Micro-cache in memoria per lo stato auth
let fetchedOnce = false;
let cachedAuth = false;
let cachedUser = null;

export const setAuth = (val) => {
  cachedAuth = val;
  if (!val) {
    cachedUser = null;
    store.commit("CLEAR_CURRENT_USER");
  }
};
export const authIsCached = () => cachedAuth;

// ---------------------------------------------------------------------------
// Definizione rotte con meta requiresAuth / requiresGuest
// ---------------------------------------------------------------------------
const routes = [
  // Catch-all (mantengo il tuo NotFound, ma puoi anche redirigere a /login)
  {
    path: "/:pathMatch(.*)*",
    component: NotFound,
    meta: { requiresAuth: false, requiresGuest: false },
  },

  // Root → /login
  {
    path: "/",
    redirect: "/login",
  },

  // ---- Area pubblica (guest) ----
  {
    path: "/login",
    name: "login",
    component: LoginPage,
    meta: {
      breadcrumb: "Login",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresGuest: true, // <--- solo utenti NON loggati
    },
    children: [
      {
        path: "verify-email",
        name: "send-email",
        component: SendEmailPage,
        meta: {
          breadcrumb: "Recupera Password",
          hideAccountLink: true,
          hideLogout: true,
          hideUrlIndex: false,
          showLogo: true,
          requiresGuest: true,
        },
      },
      {
        path: "reset-password",
        name: "set-new-password",
        component: SetNewPasswordPage,
        meta: {
          breadcrumb: "Reimposta Password",
          hideAccountLink: true,
          hideLogout: true,
          hideUrlIndex: false,
          showLogo: true,
          requiresGuest: true,
        },
      },
      {
        path: "/",
        redirect: "/login",
      },
    ],
  },

  // ---- Area privata (richiede login) ----
  {
    path: "/home",
    name: "home",
    component: HomePage,
    meta: {
      breadcrumb: "Home",
      hideAccountLink: false,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true, // <--- TUTTO /home è protetto
    },
    children: [
      {
        path: "account",
        name: "account",
        component: AccountPage,
        meta: {
          breadcrumb: "Account",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: "clienti",
        name: "customers",
        component: ClientPage,
        meta: {
          breadcrumb: "Clienti",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
        children: [
          {
            path: "nuovo",
            name: "add-client",
            component: CreateClientPage,
            meta: {
              breadcrumb: "Nuovo cliente",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "modifica/:clientId",
            name: "edit-client",
            component: EditClientPage,
            props: true,
            meta: {
              breadcrumb: "Modifica cliente",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
        ],
      },
      {
        path: "stock",
        redirect: "/home/listini",
      },
      {
        path: "listini",
        name: "listini",
        component: ListiniPage,
        meta: {
          breadcrumb: "Listini",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
        },
        children: [
          {
            path: ":category",
            name: "listini-category",
            component: ListiniCategoryPage,
            props: true,
            meta: {
              breadcrumb: "Categoria",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: true,
              requiresAuth: true,
            },
            children: [
              {
                path: "nuovo",
                name: "listini-add-item",
                component: CreateItemPage,
                meta: {
                  breadcrumb: "Nuovo articolo",
                  hideAccountLink: false,
                  hideUrlIndex: false,
                  showLogo: false,
                  requiresAuth: true,
                },
              },
              {
                path: "modifica/:itemId",
                name: "listini-edit-item",
                component: EditItemPage,
                props: true,
                meta: {
                  breadcrumb: "Modifica articolo",
                  hideAccountLink: false,
                  hideUrlIndex: false,
                  showLogo: false,
                  requiresAuth: true,
                },
              },
            ],
          },
        ],
      },
      {
        path: "magazzino",
        name: "magazzino",
        component: MagazzinoPage,
        meta: {
          breadcrumb: "Magazzino",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
        },
      },
      {
        path: "preventivi",
        name: "quotes",
        component: QuotePage,
        meta: {
          breadcrumb: "Preventivi",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
        children: [
          {
            path: "nuovo",
            name: "add-quote",
            component: CreateQuotePage,
            meta: {
              breadcrumb: "Nuovo preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: "dettaglio/:quoteId",
            name: "view-quote",
            component: EditQuotePage,
            props: (route) => ({
              quoteId: route.params.quoteId,
              readOnly: true,
            }),
            meta: {
              breadcrumb: "Dettaglio preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
            },
          },
          {
            path: "modifica/:quoteId",
            name: "edit-quote",
            component: EditQuotePage,
            props: true,
            meta: {
              breadcrumb: "Modifica preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
        ],
      },
      {
        path: "carico-merce",
        name: "carico-merce",
        component: OrderPage,
        meta: {
          breadcrumb: "Carico Merce",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
        },
      },
      {
        path: "ordini",
        name: "ordini",
        component: OrdiniPage,
        meta: {
          breadcrumb: "Ordini",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
        children: [
          {
            path: "dettaglio/:quoteId",
            name: "view-order",
            component: EditQuotePage,
            props: (route) => ({
              quoteId: route.params.quoteId,
              readOnly: true,
            }),
            meta: {
              breadcrumb: "Dettaglio ordine",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
            },
          },
          {
            path: "modifica/:quoteId",
            name: "edit-order",
            component: EditQuotePage,
            props: true,
            meta: {
              breadcrumb: "Modifica ordine",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes,
});

// ---------------------------------------------------------------------------
// Navigation guard globale
// ---------------------------------------------------------------------------
router.beforeEach(async (to) => {
  // (0) Sezione Listini. In modalità demo è chiusa (il CSS ne nasconde il
  // punto di accesso, questa guardia impedisce di arrivarci incollando l'URL);
  // fuori dalla demo esiste una sola scheda e gli URL verso le vecchie
  // categorie merceologiche vengono rifiutati. La decisione è in
  // `listiniRedirectFor`, che è pura e coperta da test.
  const listiniRedirect = listiniRedirectFor(to);
  if (listiniRedirect) {
    return { name: listiniRedirect, replace: true };
  }

  // (A) Cleanup: evita catene /login?redirect=/login?redirect=...
  if (
    to.name === "login" &&
    typeof to.query.redirect === "string" &&
    to.query.redirect.startsWith("/login")
  ) {
    const rest = { ...to.query };
    delete rest.redirect; // rimuove il parametro senza creare variabili inutilizzate
    return { name: "login", query: rest, replace: true };
  }

  const needsGuest = to.matched.some((r) => r.meta?.requiresGuest);
  const needsAuth = to.matched.some((r) => r.meta?.requiresAuth);
  const needsAdmin = to.matched.some((r) => r.meta?.requiresAdmin);

  // Rotte "neutre"
  if (!needsGuest && !needsAuth) return true;

  // (B) Ping al BE: alla prima navigazione, o dopo login (cachedAuth=true ma user non ancora caricato)
  if (!fetchedOnce || (cachedAuth && !cachedUser)) {
    try {
      const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
      cachedAuth = true;
      cachedUser = data;
      store.commit("SET_CURRENT_USER", data);
    } catch {
      cachedAuth = false;
      cachedUser = null;
      store.commit("CLEAR_CURRENT_USER");
    } finally {
      fetchedOnce = true;
    }
  }

  // (C) Regole di redirect
  if (needsAuth && !cachedAuth) {
    if (to.name === "login") return true;
    return { name: "login", query: { redirect: to.fullPath }, replace: true };
  }

  if (needsGuest && cachedAuth) {
    return { name: "home", replace: true };
  }

  if (needsAdmin && !cachedUser?.isAdmin) {
    return { name: "home", replace: true };
  }

  return true;
});

// ---------------------------------------------------------------------------
// Interceptor Axios: se il token scade (401) → chiedi di rifare login
// ---------------------------------------------------------------------------

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const current = router.currentRoute.value;
    const url = err?.config?.url || "";

    const isOnLogin = current?.name === "login";
    const isAuthEndpoint = url.startsWith("/auth/");

    if (status === 401) {
      setAuth(false);

      // Evita loop: se sei su /login o l’endpoint è pubblico, non reindirizzare
      if (!isOnLogin && !isAuthEndpoint) {
        router.replace({
          name: "login",
          query: { redirect: current.fullPath },
        });
      }
    } else if (status === 403) {
      // opzionale: porta alla home (o pagina 403) solo se non sei già su login
      if (!isOnLogin) router.replace({ name: "home" });
    }
    return Promise.reject(err);
  }
);

export default router;
